import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/shared/components/ui/badge/Badge';
import { Button } from '@/shared/components/ui/button/Button';
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card/Card';
import { Table } from '@/shared/components/ui/table/Table';
import { DOCUMENT_TYPE_LABELS } from '../../utils/documentTypeLabels';
import type { ProductSlot } from '../../types/templateAssignments.types';
import { AssignedTemplateLabel } from './AssignedTemplateLabel';
import { AssignmentActions } from './AssignmentActions';
import type { ProductDocumentsLayoutProps } from './productDocumentsView.types';

type Props = Pick<
  ProductDocumentsLayoutProps,
  'slots' | 'dealerNames' | 'isResolvingDealers' | 'canAdd' | 'canEdit' | 'canDelete' | 'onAssignProductSlot' | 'onChange' | 'onUnassign'
>;

export function ProductTemplateSlots({ slots, dealerNames, isResolvingDealers = false, canAdd, canEdit, canDelete, onAssignProductSlot, onChange, onUnassign }: Props) {
  const columns = useMemo<ColumnDef<ProductSlot>[]>(
    () => [
      {
        id: 'document',
        header: 'Document',
        cell: ({ row }) => <span className="font-medium">{DOCUMENT_TYPE_LABELS[row.original.documentType]}</span>,
      },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) =>
          row.original.assignment ? (
            <AssignedTemplateLabel
              template={row.original.assignment.template}
              dealerNames={dealerNames}
              isResolvingDealers={isResolvingDealers}
            />
          ) : (
            <Badge variant="warning" size="xs">Not assigned</Badge>
          ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const { assignment, documentType } = row.original;
          const label = DOCUMENT_TYPE_LABELS[documentType];
          if (assignment) {
            return (
              <AssignmentActions
                label={label}
                canAdd={canAdd}
                canEdit={canEdit}
                canDelete={canDelete}
                onChange={() => onChange(assignment)}
                onUnassign={() => onUnassign(assignment)}
              />
            );
          }
          return canAdd ? (
            <div className="flex justify-end">
              <Button type="button" size="sm" ariaLabel={`Assign ${label}`} onClick={() => onAssignProductSlot(documentType)}>
                Assign
              </Button>
            </div>
          ) : null;
        },
      },
    ],
    [dealerNames, isResolvingDealers, canAdd, canEdit, canDelete, onAssignProductSlot, onChange, onUnassign],
  );

  return (
    <Card ariaLabelledBy="product-templates-title">
      <CardHeader>
        <CardTitle id="product-templates-title">Product templates</CardTitle>
        <p className="mt-1 text-sm text-mp-muted">
          Sent for every agreement sold on this product, unless a dealer override applies.
        </p>
      </CardHeader>
      <Table<ProductSlot>
        data={[...slots]}
        columns={columns}
        getRowId={(row) => row.documentType}
        framed={false}
      />
    </Card>
  );
}
